import React, { memo, useCallback, useState } from 'react'

import { Notice, Icon, AIGeneratedGradient } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { useLocation } from 'Pods/Shared/ContextBasedRouter'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

const SEEN_KEY = 'remixModeNoticeSeen'

const hasBeenSeen = () => {
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1'
  } catch (_) {
    return false
  }
}

const markSeen = () => {
  try {
    window.localStorage.setItem(SEEN_KEY, '1')
  } catch (_) {
    // Storage may be unavailable (private mode, quota). Treat as no-op.
  }
}

// "You are in Remix Mode" notice, shown whenever the user opens a community model
// from the gallery. Origin rides on the navigation itself: Gallery navigates with
// `state: { fromGallery: true }`, so any other way of opening a model (library card,
// deep link, refresh) has no such state and the notice stays hidden - no flag to
// reset anywhere. It is not tied to a specific model: opening another gallery model
// is a fresh navigation that re-flags fromGallery, so it shows again until dismissed.
// The seen flag is persisted only when the user closes the notice - not when it
// merely displays - so a transient render (e.g. a route remount) never burns the
// one-time flag; once closed, a direct visit or refresh never triggers it again.
function RemixModeNotice() {
  const { isCurrentUser: isLoggedIn } = usePageConfig() || {}
  const location = useLocation()
  const fromGallery = location?.state?.fromGallery === true
  const openedModelId = useThreeDStudioStore(state => state.ui?.openedModel?.id)
  const [dismissed, setDismissed] = useState(hasBeenSeen)

  const handleClose = useCallback(() => {
    markSeen()
    setDismissed(true)
  }, [])

  if (!isLoggedIn || !fromGallery || openedModelId == null || dismissed) {
    return null
  }

  return (
    <div className="remix-mode-notice" role="status" aria-live="polite">
      <Notice
        useSSRIcon
        ssrIcon={<Icon svgComponent={AIGeneratedGradient} notSpaced size={iconSizeMedium} />}
        onClose={handleClose}
      >
        <div className="remix-mode-notice__body">
          <span className="cgt-heading-50">You are in Remix Mode</span>
          <span className="cgt-body-base">
            Adjust settings, regenerate, and enhance this 3D model to create your own version.
            New creations will be saved to your library.
          </span>
        </div>
      </Notice>
    </div>
  )
}

export default memo(RemixModeNotice)
